import { ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const fallbackPosts = [{
  id: "fallback-1",
  title: "Le repos postnatal : pourquoi est-ce essentiel ?",
  excerpt: "Comprendre les besoins physiologiques et émotionnels de la jeune maman durant les premières semaines après l'accouchement pour une récupération optimale.",
  date: "2025.03.18",
  readTime: "12 MIN",
  category: "BIEN-ÊTRE",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719083/img/repos_postnatal_maman_bebe.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719083/img/repos_postnatal_maman_bebe.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719083/img/repos_postnatal_maman_bebe.webp-wvc-sizes",
  link: "#"
}, {
  id: "fallback-2",
  title: "Batch cooking : organiser ses repas de jeune maman",
  excerpt: "Des astuces simples pour préparer des repas sains et nourrissants à l'avance et alléger votre quotidien.",
  date: "2025.02.27",
  readTime: "6 MIN",
  category: "NUTRITION",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719083/img/batch_cooking_sain.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719083/img/batch_cooking_sain.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719083/img/batch_cooking_sain.webp-wvc-sizes",
  link: "#"
}, {
  id: "fallback-3",
  title: "Le rôle du village dans le post-partum",
  excerpt: "Pourquoi s'entourer est la clé pour traverser cette période de transition avec douceur et sérénité.",
  date: "2025.02.04",
  readTime: "8 MIN",
  category: "ACCOMPAGNEMENT",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719083/img/village_soutien_maternite.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719083/img/village_soutien_maternite.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719083/img/village_soutien_maternite.webp-wvc-sizes",
  link: "#"
}];
function formatDate(dateString?: string) {
  if (!dateString) return "";
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return "";
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}.${month}.${day}`;
  } catch {
    return "";
  }
}
function estimateReadTime(content?: string) {
  if (!content) return "5 MIN";
  const text = content.replace(/<[^>]+>/g, " ");
  const words = text.trim().split(/\s+/).length;
  const minutes = Math.max(1, Math.round(words / 200));
  return `${minutes} MIN`;
}
function stripHtml(html?: string) {
  if (!html) return "";
  return html.replace(/<[^>]+>/g, "").trim();
}
function BenchReportsConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-border">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className={`bg-background ${i === 0 ? "lg:col-span-2 lg:row-span-2" : ""}`}>
            <div className="aspect-[16/10] bg-muted/30 animate-pulse" />
            <div className="p-6 space-y-4">
              <div className="h-3 w-32 bg-muted/30 animate-pulse" />
              <div className="h-8 w-3/4 bg-muted/30 animate-pulse" />
              <div className="h-4 w-full bg-muted/30 animate-pulse" />
            </div>
          </div>)}
      </div>;
  }
  const displayPosts = !error && posts && posts.length > 0 ? posts.slice(0, 3).map(post => ({
    id: post.id,
    title: post.title,
    excerpt: stripHtml(post.excerpt).slice(0, 180),
    date: formatDate(post.date),
    readTime: estimateReadTime(post.content || post.excerpt),
    category: post.categories && post.categories.length > 0 ? post.categories[0].name.toUpperCase() : "BIEN-ÊTRE",
    image: post.featuredImage || fallbackPosts[0].image,
    srcset: "",
    sizes: "",
    link: post.link || "#",
    isWpPost: true
  })) : fallbackPosts.map(p => ({
    ...p,
    isWpPost: false
  }));
  while (displayPosts.length < 3) {
    const idx = displayPosts.length;
    displayPosts.push({
      ...fallbackPosts[idx],
      isWpPost: false
    } as any);
  }
  const featured = displayPosts[0];
  const secondary = displayPosts.slice(1, 3);
  return <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <Link to={featured.link || "#"} data-index={0} className="group lg:col-span-2 lg:row-span-2 rounded-lg overflow-hidden bg-card flex flex-col">
        <div className="relative w-full overflow-hidden aspect-[4/5]">
          {(featured as any).isWpPost ? <img src={featured.image} alt={featured.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-[600ms] ease-out group-hover:scale-[1.03]" /> : <img src={featured.image} data-wvc-srcset={(featured as any).srcset} data-wvc-sizes={(featured as any).sizes} alt={featured.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-[600ms] ease-out group-hover:scale-[1.03]" />}
        </div>
        <div className="flex flex-col gap-5 p-8 flex-1">
          <div className="inline-flex w-fit items-center gap-2">
            <span className="font-default text-xs tracking-[0.2em] uppercase text-primary">
              {featured.category}
            </span>
          </div>
          <h3 className="font-default text-4xl leading-[1.1] font-normal text-foreground transition-colors duration-300 group-hover:text-primary">
            {featured.title}
          </h3>
          <p className="text-lg leading-relaxed text-brun max-w-2xl">
            {featured.excerpt}
          </p>
          <div className="mt-auto pt-4 flex items-center justify-between border-t border-beige">
            <div className="flex items-center gap-4 font-sans text-xs tracking-wider text-brun">
              <span>{featured.date}</span>
              <span className="text-beige">/</span>
              <span>{featured.readTime}</span>
            </div>
            <span className="font-sans text-xs tracking-[0.2em] text-brun inline-flex items-center gap-1">
              LIRE
              <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </span>
          </div>
        </div>
      </Link>

      {secondary.map((post, idx) => <Link key={post.id} to={post.link || "#"} data-index={idx + 1} className="group rounded-lg overflow-hidden bg-card flex flex-col">
          <div className="relative w-full overflow-hidden aspect-[4/5]">
            {(post as any).isWpPost ? <img src={post.image} alt={post.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-[600ms] ease-out group-hover:scale-[1.03]" /> : <img src={post.image} data-wvc-srcset={(post as any).srcset} data-wvc-sizes={(post as any).sizes} alt={post.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-[600ms] ease-out group-hover:scale-[1.03]" />}
          </div>
          <div className="flex flex-col gap-3 p-6 flex-1">
            <span className="font-default text-xs tracking-[0.2em] uppercase text-primary">
              {post.category}
            </span>
            <h3 className="font-default text-2xl leading-tight font-normal text-foreground transition-colors duration-300 group-hover:text-primary">
              {post.title}
            </h3>
            <div className="mt-auto pt-3 flex items-center gap-3 font-sans text-[11px] tracking-wider text-brun border-t border-beige">
              <span>{post.date}</span>
              <span className="text-beige">/</span>
              <span>{post.readTime}</span>
            </div>
          </div>
        </Link>)}
    </div>;
}
export default function Blog() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return;
}